import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { useAuth } from "./hooks/useAuth";
import { useSkin } from "./context/SkinContext";
import Sidebar from "./components/Sidebar";
import TopHeader from "./components/TopHeader";
import LoginForm from "./pages/LoginForm";
import SelectDatabase from "./pages/SelectDatabase";
import Dashboard from "./pages/Dashboard";
import TableStatus from "./pages/TableStatus";
import ReportTable from "./pages/ReportTable";
import Consumption from "./pages/Consumption";
import Settings from "./pages/Settings";
import Contact from "./pages/Contact";
import Profile from "./pages/Profile";
import Help from "./pages/Help";

const PAGE_TITLES = {
  "/": "Kontrolna tabla",
  "/kontrolna-tabla": "Kontrolna tabla",
  "/tables": "Zauzetost stolova",
  "/report": "Promet artikala",
  "/consumption": "Nabavka",
  "/settings": "Podešavanja",
  "/help": "Pomoć",
  "/kontakt": "Kontakt",
  "/profile": "Profil",
};

function AppShell({ user, onLogout }) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  const title = PAGE_TITLES[location.pathname] || "Izveštaji";
  const { syncFromServer } = useSkin();
  useEffect(() => {
    syncFromServer();
  }, [syncFromServer]);

  // Escape closes the side menu on mobile
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e) => e.key === "Escape" && setMobileNavOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileNavOpen]);

  return (
    <div className="flex h-dvh overflow-hidden bg-canvas dark:bg-slate-950">
      <a
        href="#glavni-sadrzaj"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-modal focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
      >
        Preskoči na sadržaj
      </a>

      {/* The sidebar is fixed on lg+ and slides over the content as a panel below that */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      {mobileNavOpen && (
        <div className="fixed inset-0 z-drawer lg:hidden">
          <div className="absolute inset-0 animate-fade-in bg-slate-950/50" onClick={() => setMobileNavOpen(false)} aria-hidden="true" />
          <div className="absolute inset-y-0 left-0 animate-slide-in shadow-pop dark:shadow-pop-dark">
            <Sidebar onNavigate={() => setMobileNavOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <TopHeader
          title={title}
          user={user}
          onLogout={onLogout}
          onMenuClick={() => setMobileNavOpen(true)}
        />
        <main id="glavni-sadrzaj" tabIndex={-1} className="scroll-thin flex-1 overflow-y-auto focus:outline-none">
          <div className="mx-auto w-full max-w-[1480px] px-4 py-5 sm:px-6">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/kontrolna-tabla" element={<Navigate to="/" replace />} />
              <Route path="/tables" element={<TableStatus />} />
              <Route path="/report" element={<ReportTable />} />
              <Route path="/consumption" element={<Consumption />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/help" element={<Help />} />
              <Route path="/kontakt" element={<Contact />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}

function App() {
  const { user, login, logout } = useAuth();
  const needsDbSelection = user && user.needsDbSelection && !user.dbSelected;

  return (
    <ThemeProvider>
      <Router>
        {!user ? (
          <LoginForm onLoginSuccess={login} />
        ) : needsDbSelection ? (
          <SelectDatabase user={user} onSelected={() => login({ ...user, dbSelected: true })} />
        ) : (
          <AppShell user={user} onLogout={logout} />
        )}
      </Router>
    </ThemeProvider>
  );
}

export default App;
